import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Integration, Rights, useIntegrationsQuery } from '../../graphql/types';
import { Autocomplete, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import AssetListButtonOption from '../AssetListButtonOption';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';
import useAuth from '../../hooks/useAuth';

export const SearchIntegrationAutocomplete: React.FC<{
  onChange: (value: string | null) => void;
  value?: string | null;
}> = ({ onChange, value: valueId }) => {
  const { user: currentUser } = useAuth();
  const inputReference = useRef<HTMLInputElement>(null);
  const [integrations, setIntegrations] = useState<readonly Integration[]>([]);
  const [integrationInputValue, setIntegrationInputValue] = useState<string>('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const {
    loading: integrationsLoading,
    data: integrationsData
  } = useIntegrationsQuery({
    variables: {},
    fetchPolicy: 'network-only',
    skip:
      !currentUser?.rights.includes(Rights.ListIntegrations) &&
      !currentUser?.rights.includes(Rights.ManageIntegrations) &&
      !currentUser?.rights.includes(Rights.EditMca) &&
      !currentUser?.rights.includes(Rights.CreateMca) &&
      !currentUser?.rights.includes(Rights.EditOffer)
  });
  useEffect(
    () => setIntegrations(((integrationsData?.integrations.data as Integration[]) || []).filter((integration) => integration.autostart)),
    [integrationsData?.integrations.data, integrationsLoading]
  );
  useEffect(() => {
    if (valueId && integrations.length > 0) {
      const match = integrations.find((i) => i.id === valueId);
      setSelectedIntegration(match || null);
    } else if (!valueId) {
      setSelectedIntegration(null);
    }
  }, [valueId, integrations]);
  return (
    <Autocomplete
      value={selectedIntegration}
      getOptionLabel={(option: Integration) => option.name || ''}
      options={integrations}
      autoComplete
      isOptionEqualToValue={(option: Integration, value: Integration) => option.id === value?.id}
      onChange={(event: SyntheticEvent, newValue: Integration | null) => {
        setSelectedIntegration(newValue);
        onChange(newValue?.id || null);
      }}
      onInputChange={(event: SyntheticEvent, newInputValue: string) => {
        setIntegrationInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={'Select Platform'}
          inputRef={inputReference}
          name="integration"
          sx={{ minWidth: '280px', pt: 0.125, pb: 0.125 }}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: selectedIntegration?.id && (
              <PlatformCompanyLogo integration={selectedIntegration} size={'xs'} style={{ margin: '-2px 0px 0px 3px' }} />
            ),
            style: {
              height: '34.25px',
              fontSize: '11px'
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <AssetListButtonOption
            logo={option.logo || null}
            logo2={option?.company?.logo || undefined}
            name={option.name || ''}
            type={'Integration'}
            {...props}
          />
        );
      }}
    />
  );
};
